import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { animated, useSpring } from '@react-spring/web';
import { useWindowWidth } from '@wojtekmaj/react-hooks';
import T from '@wojtekmaj/react-t';
import { AwardIcon, HeartIcon, HomeIcon, MailIcon } from 'lucide-react';

import {
  Badge,
  Icon,
  Label,
  List,
  ListItem,
  ListItemLink,
  ListItemUnderlay,
  TabIndicator,
  Wrapper,
} from './menu.styles';

import useVendorBusinessFlag from '../../../hooks/useVendorBusinessFlag';

import { unreadVendorMessagesCountLoadable } from '../../../store';

const AnimatedTabIndicator = animated(TabIndicator);

type Item = {
  label: string;
  icon: React.ComponentType;
  href: string;
  exact?: boolean;
};

type ItemWithBadge = Item & { badge?: number };

type ItemWithBadgeAndFlags = ItemWithBadge & { active: boolean };

function areRectsEqual(prevRect: DOMRect | null, newRect: DOMRect): boolean {
  return Boolean(prevRect && prevRect.x === newRect.x && prevRect.width === newRect.width);
}

type MenuItemProps = {
  item: ItemWithBadgeAndFlags;
  onActive: (layout: DOMRect, parentLayout: DOMRect) => void;
};

function MenuItem({ item, onActive }: MenuItemProps) {
  const [domRect, setDomRect] = useState<DOMRect | null>(null);
  const [parentDomRect, setParentDomRect] = useState<DOMRect | null>(null);

  const { label, icon: IconComponent, href, exact, badge, active } = item;

  useEffect(() => {
    if (!active || !domRect || !parentDomRect) {
      return;
    }

    onActive(domRect, parentDomRect);
  }, [active, domRect, onActive, parentDomRect]);

  return (
    <ListItem
      ref={(ref) => {
        if (!ref) {
          return;
        }

        function getRect() {
          if (!ref) {
            return;
          }

          const parentElement = ref.parentElement;

          if (!parentElement) {
            return;
          }

          const rect = ref.getBoundingClientRect();
          const parentRect = parentElement.getBoundingClientRect();

          setDomRect((prevDomRect) => (areRectsEqual(prevDomRect, rect) ? prevDomRect : rect));

          setParentDomRect((prevParentRect) =>
            areRectsEqual(prevParentRect, parentRect) ? prevParentRect : parentRect,
          );
        }

        getRect();

        document.fonts.addEventListener('loadingdone', getRect);
      }}
    >
      <ListItemLink end={exact} to={href}>
        <ListItemUnderlay />
        <Icon>
          <IconComponent />
        </Icon>
        <Label>
          <T>{label}</T>
        </Label>
        {badge ? <Badge>{badge}</Badge> : null}
      </ListItemLink>
    </ListItem>
  );
}

export default function Menu() {
  const windowWidth = useWindowWidth();
  const location = useLocation();
  const shouldDisplayChallenges = useVendorBusinessFlag('challenges');
  const unreadVendorMessagesCountLoadableValue = useAtomValue(unreadVendorMessagesCountLoadable);
  const shouldUseImmediate = useRef(true);
  const [indicatorRect, setIndicatorRect] = useState<object>({ left: 0, opacity: 1 });

  const props = useSpring(indicatorRect);

  const unreadVendorMessagesCount =
    unreadVendorMessagesCountLoadableValue.state === 'hasData'
      ? unreadVendorMessagesCountLoadableValue.data
      : null;

  const items = useMemo<Item[]>(
    () =>
      [
        {
          label: 'Home',
          icon: HomeIcon,
          href: '/',
          exact: true,
        },
        {
          label: 'Favorites',
          icon: HeartIcon,
          href: '/favorites',
        },
        shouldDisplayChallenges && {
          label: 'Sweet &\u00a0Fun',
          icon: AwardIcon,
          href: '/challenges',
        },
        {
          label: 'Messages',
          icon: MailIcon,
          href: '/inbox',
        },
      ].filter(Boolean),
    [shouldDisplayChallenges],
  );

  const itemsWithBadge = useMemo<ItemWithBadge[]>(
    () =>
      unreadVendorMessagesCount
        ? items.map((item) =>
            item.href === 'inbox' ? { ...item, badge: unreadVendorMessagesCount } : item,
          )
        : items,
    [items, unreadVendorMessagesCount],
  );

  const itemsWithBadgeAndFlags = useMemo<ItemWithBadgeAndFlags[]>(
    () =>
      itemsWithBadge.map((item) => {
        const pattern = item.exact ? item.href : `${item.href}/*`;
        const match = matchPath({ path: pattern, end: item.exact }, location.pathname);

        return { ...item, active: Boolean(match) };
      }),
    [itemsWithBadge, location.pathname],
  );

  const activeItem = itemsWithBadgeAndFlags.find((item) => item.active);
  const hasActiveItem = Boolean(activeItem);

  useEffect(() => {
    if (hasActiveItem) {
      return;
    }

    setIndicatorRect({
      opacity: 0,
      immediate: true,
    });

    shouldUseImmediate.current = true;
  }, [hasActiveItem]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect intentionally triggered on window width change
  useEffect(() => {
    shouldUseImmediate.current = true;
  }, [windowWidth]);

  const onActive = useCallback((rect: DOMRect, parentRect: DOMRect) => {
    const immediate = shouldUseImmediate.current;

    setIndicatorRect({
      left: rect.x - parentRect.x + rect.width / 2,
      opacity: 1,
      immediate,
    });

    shouldUseImmediate.current = false;
  }, []);

  useEffect(() => {
    if (unreadVendorMessagesCount) {
      if ('setAppBadge' in navigator) {
        navigator.setAppBadge(unreadVendorMessagesCount);
      }
    } else {
      if ('clearAppBadge' in navigator) {
        navigator.clearAppBadge();
      }
    }
  }, [unreadVendorMessagesCount]);

  return (
    <Wrapper>
      <List itemCount={itemsWithBadgeAndFlags.length} role="list">
        {itemsWithBadgeAndFlags.map((item) => (
          <MenuItem item={item} key={item.href} onActive={onActive} />
        ))}
      </List>
      <AnimatedTabIndicator style={props} />
    </Wrapper>
  );
}
